// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Page shell for the login gateway. Owns the step state (phone | otp | help), the tab header,
// the live-region announcements and focus management; the step contents live in
// components/welcome/AuthSteps.tsx and the OTP logic in hooks/useOtpLogin.ts.
// After a successful OTP it sends the customer on to /welcome/profile (router state `fromLogin`).
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { CircleCheck, Lock, ShieldCheck } from "lucide-react";
import PageTransition from "../components/PageTransition";
import HeroPanel from "../components/welcome/HeroPanel";
import WelcomeHeader from "../components/welcome/WelcomeHeader";
import { HelpStep, OtpStep, PhoneStep, SignedInStep } from "../components/welcome/AuthSteps";
import { COPY, FACTS, SUPPORT_LINK, type StepId } from "../data/welcome";
import useAuth from "../hooks/useAuth";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useOtpLogin from "../hooks/useOtpLogin";
import usePrefersReducedMotion from "../hooks/usePrefersReducedMotion";
import { chooseGuest } from "../utils/entryGate";
import { maskNationalNumber } from "../utils/phone";

/** A login gateway is not something search engines should list. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

const TRUST = [
  { icon: Lock, title: "Encrypted", detail: "HTTPS in transit" },
  { icon: ShieldCheck, title: "No spam", detail: "Login codes only" },
  { icon: CircleCheck, title: "No password", detail: "Sign in with an SMS code" },
];

const SUCCESS_REDIRECT_MS = 1200;

export default function Welcome() {
  useDocumentTitle("Welcome");
  useNoIndex();

  const navigate = useNavigate();
  const auth = useAuth();
  const login = useOtpLogin();
  const reducedMotion = usePrefersReducedMotion();
  const [step, setStep] = useState<StepId>("phone");
  const panelRef = useRef<HTMLDivElement>(null);
  // Only steps the visitor reached by acting *inside* the panel (send code, back, change
  // number) pull focus into it. Moving between tabs must leave focus on the tab, or the next
  // arrow-key press would go nowhere (WAI-ARIA tabs pattern).
  const focusPanelNext = useRef(false);
  const redirectTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [signOutError, setSignOutError] = useState<string | null>(null);

  const signedIn = auth.status === "signed-in";
  const otpAvailable = login.hasSentCode;

  useEffect(() => () => clearTimeout(redirectTimer.current), []);

  // After an in-panel transition, focus the new step's heading so keyboard and screen-reader
  // users hear what changed instead of being left on a control that has just disappeared.
  // AnimatePresence swaps the content after the outgoing step fades, so poll for it (the same
  // approach App.tsx uses for hash links) rather than assuming it is already mounted.
  useEffect(() => {
    if (!focusPanelNext.current) return;
    let cancelled = false;
    const deadline = Date.now() + 800;
    const tryFocus = () => {
      if (cancelled) return;
      const heading = panelRef.current?.querySelector<HTMLElement>(`[data-step="${step}"] h1`);
      if (heading) heading.focus();
      else if (Date.now() < deadline) requestAnimationFrame(tryFocus);
    };
    tryFocus();
    return () => {
      cancelled = true;
    };
  }, [step]);

  const goTo = useCallback((next: StepId) => {
    focusPanelNext.current = true;
    setStep(next);
  }, []);

  const goToStore = useCallback(() => {
    chooseGuest();
    navigate("/");
  }, [navigate]);

  const selectStep = useCallback(
    (next: StepId) => {
      if (next === "otp" && !otpAvailable) return;
      focusPanelNext.current = false; // came from the tab list: focus stays on the tab
      setStep(next);
    },
    [otpAvailable],
  );

  const handleSignOut = async () => {
    setSignOutError(null);
    try {
      await auth.signOut();
    } catch {
      setSignOutError("We couldn't sign you out. Check your connection and try again.");
    }
  };

  // A polite, always-present live region: button-label swaps ("Sending…") and the success
  // screen are otherwise silent to screen readers.
  const announcement =
    login.status === "sending"
      ? "Sending your code by SMS…"
      : login.status === "verifying"
        ? "Checking your code…"
        : login.status === "success"
          ? "You're signed in. Taking you to the store."
          : step === "otp" && login.hasSentCode
            ? `Code sent to ${maskNationalNumber(login.nationalNumber)}. Enter the 6 digits.`
            : "";

  const sendCode = async () => {
    if (await login.sendCode()) goTo("otp");
  };

  const verify = async (code: string) => {
    if (await login.verifyCode(code)) {
      // A first-time sign-in goes on to the policy-consent step; returning users skip it.
      redirectTimer.current = setTimeout(
        () => navigate("/welcome/profile", { state: { fromLogin: true } }),
        SUCCESS_REDIRECT_MS,
      );
    }
  };

  const changeNumber = () => {
    login.changeNumber();
    goTo("phone");
  };

  const panel = (() => {
    switch (step) {
      case "otp":
        return (
          <OtpStep
            nationalNumber={login.nationalNumber}
            status={login.status}
            error={login.error}
            secondsLeft={login.secondsLeft}
            onVerify={verify}
            onResend={login.sendCode}
            onChangeNumber={changeNumber}
            onClearError={login.clearError}
          />
        );
      case "help":
        return <HelpStep onGuest={goToStore} />;
      default:
        return signedIn ? (
          <SignedInStep
            phone={auth.user?.phone ?? null}
            error={signOutError}
            onContinue={() => navigate("/")}
            onSignOut={handleSignOut}
          />
        ) : (
          <PhoneStep
            nationalNumber={login.nationalNumber}
            status={login.status}
            error={login.error}
            onNumber={login.updateNumber}
            onSubmit={sendCode}
            onGuest={goToStore}
          />
        );
    }
  })();

  return (
    <PageTransition>
      {/* pt-8: DisclaimerBanner is a fixed 2rem strip across the top of every page. */}
      <div className="min-h-screen bg-foam-50 pt-8 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
        <a
          href="#welcome-panel"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-10 focus:z-[80] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:font-semibold focus:text-ocean-950 focus:shadow-lg"
        >
          Skip to sign in
        </a>
        <p role="status" aria-live="polite" className="sr-only">
          {announcement}
        </p>
        <WelcomeHeader
          step={step}
          onStep={selectStep}
          otpAvailable={otpAvailable}
          signedIn={signedIn}
          onReturnToStore={goToStore}
        />

        <div className="mx-auto max-w-[1500px] px-4 pb-6 pt-4 sm:px-8">
          <div className="grid overflow-hidden rounded-[2rem] bg-white shadow-xl shadow-ocean-900/10 dark:bg-ocean-900 lg:grid-cols-[7fr_5fr]">
            {/* The login is first in the DOM, so phones and screen readers reach it before the
                marketing copy; on desktop the hero is placed on the left with CSS order. */}
            <div className="flex flex-col p-6 sm:p-10 lg:order-2">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[11px] font-bold uppercase tracking-widest text-ocean-800/70 dark:text-foam-100/60">
                  {COPY.authLabel}
                </p>
                <Link to="/welcome/staff" className="text-xs font-bold text-ocean-800 underline dark:text-foam-100">Staff sign in</Link>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-100 px-3 py-1.5 text-xs font-bold text-teal-700">
                  <span aria-hidden="true" className="h-2 w-2 rounded-full bg-teal-500" />
                  Quick Auth
                </span>
              </div>

              <div
                ref={panelRef}
                role="tabpanel"
                id="welcome-panel"
                aria-labelledby={`welcome-tab-${step}`}
                tabIndex={-1}
                className="my-8 flex-1 outline-none"
              >
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={step}
                    data-step={step}
                    initial={reducedMotion ? false : { opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                    transition={{ duration: reducedMotion ? 0 : 0.18 }}
                  >
                    {panel}
                  </motion.div>
                </AnimatePresence>
              </div>

              <ul className="grid grid-cols-3 gap-2 rounded-2xl bg-ocean-700/5 p-4 text-center dark:bg-white/10">
                {TRUST.map(({ icon: Icon, title, detail }) => (
                  <li key={title}>
                    <Icon className="mx-auto h-5 w-5 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                    <p className="mt-1 text-xs font-bold text-ocean-950 dark:text-foam-50">{title}</p>
                    <p className="text-[11px] text-ocean-800/80 dark:text-foam-100/70">{detail}</p>
                  </li>
                ))}
              </ul>
            </div>

            <HeroPanel className="lg:order-1" />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-2 text-sm text-ocean-900 dark:text-foam-100">
            <p className="flex items-center gap-2">
              <span aria-hidden="true" className="h-2 w-2 rounded-full bg-teal-500" />
              {FACTS.liveHubs} hubs live across India&apos;s coasts • Fresh catch lands every morning
            </p>
            <nav aria-label="More about ZenFresh" className="flex items-center gap-4">
              <Link to="/about" className="hover:text-teal-700 hover:underline dark:hover:text-teal-300">
                How your catch is traced
              </Link>
              <span aria-hidden="true">•</span>
              <a href={SUPPORT_LINK} target="_blank" rel="noreferrer" className="hover:text-teal-700 hover:underline dark:hover:text-teal-300">
                Chat with support<span className="sr-only"> (opens WhatsApp in a new tab)</span>
              </a>
            </nav>
          </div>
        </div>

        <footer className="border-t border-ocean-900/5 bg-white dark:border-white/10 dark:bg-ocean-900">
          <div className="mx-auto flex max-w-[1500px] flex-wrap items-center justify-between gap-x-8 gap-y-3 px-4 py-5 text-sm text-ocean-900 dark:text-foam-100 sm:px-8">
            <ul className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <li className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                {FACTS.traceability} Traceable Catch
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-teal-700 dark:text-teal-300" aria-hidden="true" />
                Cold-chain delivery
              </li>
              <li className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
                Encrypted OTP login
              </li>
            </ul>
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Link to="/privacy" className="hover:underline">
                Privacy Policy
              </Link>
              <span aria-hidden="true">•</span>
              <Link to="/terms" className="hover:underline">
                Terms of Service
              </Link>
              <span aria-hidden="true">•</span>
              <span>© {new Date().getFullYear()} ZenFresh</span>
            </p>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
}
